/**
 * Staging Pipeline Status Component
 * Shows the progress of records through the staging ETL pipeline
 * Used by both Program Import and Provider Import managers
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import {
  Database,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Clock,
  Eye,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  Filter
} from 'lucide-react';

interface StagingStats {
  total: number;
  staged: number;
  transformed: number;
  validated: number;
  loaded: number;
  failed: number;
  pending_review: number;
  duplicates: number;
}

interface StagingRecord {
  id: number;
  external_id: string;
  stage: string;
  source_name: string;
  validation_passed: boolean;
  validation_errors: string[];
  needs_review: boolean;
  review_reason: string;
  data_quality_score: number;
  field_completeness: number;
  normalized_data: any;
  created_at: string;
}

interface StagingPipelineStatusProps {
  jobId: number;
  apiBaseUrl: string;
  authToken?: string;
  onRefresh?: () => void;
}

const StagingPipelineStatus: React.FC<StagingPipelineStatusProps> = ({
  jobId,
  apiBaseUrl,
  authToken,
  onRefresh
}) => {
  const { t } = useAppTranslation();
  const [stats, setStats] = useState<StagingStats | null>(null);
  const [reviewQueue, setReviewQueue] = useState<StagingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showReviewQueue, setShowReviewQueue] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<StagingRecord | null>(null);

  useEffect(() => {
    loadStagingStats();
  }, [jobId]);

  const loadStagingStats = async () => {
    try {
      const response = await authFetch(
        `${apiBaseUrl}/staging/jobs/${jobId}/stats`
      );

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading staging stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReviewQueue = async () => {
    try {
      const response = await authFetch(
        `${apiBaseUrl}/staging/jobs/${jobId}/review-queue`
      );

      if (response.ok) {
        const data = await response.json();
        setReviewQueue(data.records || []);
        setShowReviewQueue(true);
      }
    } catch (error) {
      console.error('Error loading review queue:', error);
    }
  };

  const handleReviewAction = async (recordId: number, action: 'APPROVE' | 'REJECT') => {
    try {
      const response = await authFetch(
        `${apiBaseUrl}/staging/records/${recordId}/review`,
        {
          method: 'POST',
          jsonBody: { action }
        }
      );

      if (response.ok) {
        // Reload data
        loadStagingStats();
        loadReviewQueue();
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      console.error('Error submitting review:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">{t('admin.loading_staging_stats', 'Loading staging stats...')}</span>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const stages = [
    { name: 'Staged', count: stats.staged, color: 'bg-blue-500' },
    { name: 'Transformed', count: stats.transformed, color: 'bg-purple-500' },
    { name: 'Validated', count: stats.validated, color: 'bg-yellow-500' },
    { name: 'Loaded', count: stats.loaded, color: 'bg-green-500' }
  ];

  const totalProcessed = stats.loaded + stats.failed + stats.pending_review + stats.duplicates;
  const successRate = stats.total > 0 ? ((stats.loaded / stats.total) * 100).toFixed(1) : '0';

  return (
    <div className="mt-4 border-t pt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-700 flex items-center">
          <Database className="h-4 w-4 mr-2" />
          {t('admin.staging_pipeline', 'Staging Pipeline')}
        </h4>
        <Button
          size="sm"
          variant="outline"
          onClick={loadStagingStats}
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          {t('admin.refresh', 'Refresh')}
        </Button>
      </div>

      {/* Pipeline Progress */}
      <div className="flex items-center gap-2 mb-4">
        {stages.map((stage, index) => (
          <React.Fragment key={stage.name}>
            <div className="flex-1 text-center">
              <div className={`h-2 rounded ${stage.color}`} style={{ opacity: stage.count > 0 ? 1 : 0.3 }} />
              <div className="mt-1">
                <div className="text-xs font-medium">{stage.count}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{stage.name}</div>
              </div>
            </div>
            {index < stages.length - 1 && (
              <ArrowRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <div className="text-center p-2 bg-green-50 rounded">
          <div className="text-lg font-semibold text-green-600">{stats.loaded}</div>
          <div className="text-xs text-green-600">{t('admin.imported', 'Imported')}</div>
        </div>
        <div className="text-center p-2 bg-yellow-50 rounded">
          <div className="text-lg font-semibold text-yellow-600">{stats.pending_review}</div>
          <div className="text-xs text-yellow-600">{t('admin.review', 'Review')}</div>
        </div>
        <div className="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
          <div className="text-lg font-semibold text-gray-600 dark:text-gray-300">{stats.duplicates}</div>
          <div className="text-xs text-gray-600 dark:text-gray-300">{t('admin.duplicates', 'Duplicates')}</div>
        </div>
        <div className="text-center p-2 bg-red-50 rounded">
          <div className="text-lg font-semibold text-red-600">{stats.failed}</div>
          <div className="text-xs text-red-600">{t('admin.failed', 'Failed')}</div>
        </div>
      </div>

      {/* Success Rate */}
      <div className="flex items-center justify-between text-sm mb-3">
        <span className="text-gray-600 dark:text-gray-300">{t('admin.success_rate', 'Success Rate')}</span>
        <span className="font-medium">{successRate}%</span>
      </div>
      <Progress value={parseFloat(successRate)} className="h-2" />

      {/* Review Queue Button */}
      {stats.pending_review > 0 && (
        <Button
          className="w-full mt-3"
          variant="outline"
          onClick={loadReviewQueue}
        >
          <Eye className="h-4 w-4 mr-2" />
          Review {stats.pending_review} Records
        </Button>
      )}

      {/* Review Queue Modal */}
      {showReviewQueue && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">{t('admin.review_queue', 'Review Queue')}</h3>
              <Button variant="ghost" onClick={() => setShowReviewQueue(false)}>
                ✕
              </Button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {reviewQueue.length === 0 ? (
                <p className="text-center text-gray-500 py-8">{t('admin.no_records_pending_review', 'No records pending review')}</p>
              ) : (
                <div className="space-y-3">
                  {reviewQueue.map(record => (
                    <div key={record.id} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium">
                            {record.normalized_data?.program_name ||
                             record.normalized_data?.organization_name ||
                             record.external_id ||
                             `Record ${record.id}`}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {record.source_name} • Quality: {record.data_quality_score}%
                          </div>
                          {record.review_reason && (
                            <div className="text-sm text-yellow-600 mt-1 flex items-center">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              {record.review_reason}
                            </div>
                          )}
                          {record.validation_errors && record.validation_errors.length > 0 && (
                            <div className="text-xs text-red-500 mt-1">
                              Errors: {record.validation_errors.join(', ')}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedRecord(record)}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            className="bg-v2accent hover:bg-v2accent/90 text-white"
                            onClick={() => handleReviewAction(record.id, 'APPROVE')}
                          >
                            <ThumbsUp className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleReviewAction(record.id, 'REJECT')}
                          >
                            <ThumbsDown className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Record Detail Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Record Details</h3>
              <Button variant="ghost" onClick={() => setSelectedRecord(null)}>
                ✕
              </Button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <pre className="text-xs bg-gray-50 dark:bg-gray-700 p-3 rounded overflow-auto">
                {JSON.stringify(selectedRecord.normalized_data, null, 2)}
              </pre>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button
                className="bg-v2accent hover:bg-v2accent/90 text-white"
                onClick={() => {
                  handleReviewAction(selectedRecord.id, 'APPROVE');
                  setSelectedRecord(null);
                }}
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                {t('admin.approve', 'Approve')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  handleReviewAction(selectedRecord.id, 'REJECT');
                  setSelectedRecord(null);
                }}
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                {t('admin.reject', 'Reject')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StagingPipelineStatus;
